import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, Moon, PanelLeftClose, PanelLeftOpen, Sun } from 'lucide-react';
import { confirmAppLeave } from '@/lib/appLeaveGuard';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { useActiveClinic } from '@/lib/ActiveClinicContext';
import { useClinic } from '@/hooks/useClinicSettings';
import { useUnreadPatientMessageCount } from '@/hooks/usePatientMessages';
import { useMyOpenTaskCount } from '@/hooks/useStaffTasks';
import { ClinicSelector } from '@/components/ClinicSelector';
import { Button } from '@/components/ui/Button';
import { Toggle } from '@/components/ui/Toggle';
import { cn } from '@/utils/cn';
import { TYPE } from '@/lib/typography';
import { readActivatedModules } from '@/lib/clinicModules';
import {
  isNavItemActive,
  navBadgeCount,
  visibleClinicNavItems,
} from '@/components/Layout/sidebarNav';
import { NAV_ICONS } from '@/components/Layout/navIcons';
import { UserAccountCard } from '@/components/Layout/UserAccountCard';
import { UnreadBadge } from '@/components/ui/UnreadBadge';
import { useUnreadUnmatchedEmailCount } from '@/hooks/usePatientEmailThreads';
import { readSidebarCollapsed, writeSidebarCollapsed } from '@/lib/sidebarCollapsed';

export function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, isPlatformAdmin, hasPermission, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { activeClinic } = useActiveClinic();
  const activeClinicSettings = useClinic(activeClinic?.id ?? '');
  const canViewMessages = Boolean(
    activeClinic?.id && hasPermission(activeClinic.id, 'messages', 'view'),
  );
  const unreadMessages = useUnreadPatientMessageCount(
    canViewMessages ? (activeClinic?.id ?? null) : null,
  );
  const canViewUnmatchedEmail = Boolean(
    activeClinic?.id && hasPermission(activeClinic.id, 'communication', 'view'),
  );
  const unreadUnmatchedEmails = useUnreadUnmatchedEmailCount(
    canViewUnmatchedEmail ? (activeClinic?.id ?? null) : null,
    canViewUnmatchedEmail ? (user?.id ?? null) : null,
  );
  const canViewTasks = Boolean(activeClinic?.id && hasPermission(activeClinic.id, 'tasks', 'view'));
  const openTasks = useMyOpenTaskCount(
    canViewTasks ? (activeClinic?.id ?? null) : null,
    canViewTasks ? (user?.id ?? null) : null,
  );
  const [collapsed, setCollapsed] = useState(() => readSidebarCollapsed(user?.id));

  useEffect(() => {
    setCollapsed(readSidebarCollapsed(user?.id));
  }, [user?.id]);

  const visibleNavItems = visibleClinicNavItems({
    isPlatformAdmin,
    clinicId: activeClinic?.id,
    hasPermission,
    activatedModules: readActivatedModules(activeClinicSettings.data?.activated_modules),
    modulesLoaded: activeClinicSettings.isSuccess,
  });

  const unreadCount = unreadMessages.data ?? 0;
  const openTaskCount = openTasks.data ?? 0;
  const unmatchedEmailCount = unreadUnmatchedEmails.data ?? 0;
  const menuToggleLabel = collapsed ? t('nav.expand_menu') : t('nav.collapse_menu');
  const themeLabel = theme === 'dark' ? t('nav.toggle_theme_light') : t('nav.toggle_theme_dark');

  const handleSignOut = () => {
    confirmAppLeave(() => {
      void (async () => {
        try {
          await signOut();
        } finally {
          navigate('/sign-in', { replace: true });
        }
      })();
    });
  };

  function toggleCollapsed() {
    setCollapsed((current) => {
      const next = !current;
      writeSidebarCollapsed(user?.id, next);
      return next;
    });
  }

  return (
    <aside
      id="staff-sidebar"
      className={cn(
        'hidden h-screen shrink-0 flex-col border-r border-ds-line bg-ds-side nav:flex',
        collapsed ? 'w-nav-collapsed' : 'w-nav',
      )}
    >
      <div className={cn(collapsed ? 'flex flex-col items-center px-s3 pt-s5' : 'px-s4 pt-s5')}>
        <div
          className={cn(
            'flex items-center',
            collapsed ? 'justify-center' : 'w-full justify-between',
          )}
        >
          {!collapsed ? <span className="ds-wordmark block px-s1">Clinio</span> : null}
          <button
            type="button"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-controls="staff-sidebar"
            aria-label={menuToggleLabel}
            title={menuToggleLabel}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-ctl border-0 bg-transparent p-0 text-ds-muted shadow-none hover:bg-ds-sunk hover:text-ds-text focus-visible:shadow-ring focus-visible:outline-none"
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            ) : (
              <PanelLeftClose className="h-4 w-4" strokeWidth={1.75} aria-hidden />
            )}
          </button>
        </div>
        <div className={cn(collapsed ? 'mt-s4' : 'mt-s5')}>
          <ClinicSelector alwaysShow collapsed={collapsed} />
        </div>
      </div>

      <nav className="mt-s4 min-h-0 flex-1 overflow-hidden px-s3" aria-label={t('design.main_nav')}>
        <ul className={cn('flex flex-col gap-0.5', collapsed && 'items-center')}>
          {visibleNavItems.map((item) => {
            const Icon = NAV_ICONS[item.icon];
            const active = isNavItemActive(item.to, location.pathname);
            const label = t(item.i18nKey);
            const unread = navBadgeCount(item.badge, {
              unreadMessages: unreadCount,
              openTasks: openTaskCount,
              unreadUnmatchedEmails: unmatchedEmailCount,
            });
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  aria-label={collapsed ? label : undefined}
                  title={collapsed ? label : undefined}
                  className={cn(
                    TYPE.control,
                    'relative flex h-10 items-center rounded-ctl',
                    collapsed ? 'w-10 justify-center' : 'gap-s3 px-s3',
                    active
                      ? 'bg-ds-teal-tint font-semibold text-ds-teal'
                      : 'font-medium text-ds-text hover:bg-ds-neutral-tint',
                  )}
                >
                  <Icon
                    className={cn('h-5 w-5 shrink-0', active ? 'text-ds-teal' : 'text-ds-muted')}
                    aria-hidden
                  />
                  {!collapsed ? <span className="truncate">{label}</span> : null}
                  <UnreadBadge
                    count={unread}
                    className={collapsed ? 'absolute right-0 top-0' : 'ml-auto'}
                  />
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className={cn('pb-s4', collapsed ? 'flex flex-col items-center px-s3' : 'px-s3')}>
        <div className={cn(collapsed && 'flex justify-center')}>
          <UserAccountCard collapsed={collapsed} />
        </div>

        <div
          className={cn(
            'mt-s3 flex flex-col gap-0.5 border-t border-ds-line pt-s3',
            collapsed && 'items-center',
          )}
        >
          {collapsed ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => toggleTheme()}
              aria-label={themeLabel}
              title={themeLabel}
            >
              {theme === 'dark' ? <Sun aria-hidden /> : <Moon aria-hidden />}
            </Button>
          ) : (
            <div className="flex h-10 items-center gap-s3 rounded-ctl px-s3">
              <Toggle
                track="switch"
                checked={theme === 'dark'}
                onCheckedChange={() => toggleTheme()}
                aria-label={themeLabel}
              />
              <span className={cn(TYPE.control, 'text-ds-text')}>{t('nav.toggle_theme_dark')}</span>
            </div>
          )}

          <button
            type="button"
            onClick={handleSignOut}
            aria-label={t('nav.sign_out')}
            title={collapsed ? t('nav.sign_out') : undefined}
            className={cn(
              TYPE.control,
              'flex items-center rounded-ctl font-medium text-ds-text hover:bg-ds-neutral-tint',
              collapsed ? 'h-10 w-10 justify-center' : 'h-10 w-full gap-s3 px-s3',
            )}
          >
            <LogOut className="h-5 w-5 text-ds-muted" aria-hidden />
            {!collapsed ? <span>{t('nav.sign_out')}</span> : null}
          </button>
        </div>
      </div>
    </aside>
  );
}
